import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import InputPanel from './components/InputPanel';
import DrawingCanvas from './components/DrawingCanvas';
import PanelSummary from './components/PanelSummary';
import Toolbar from './components/Toolbar';
import DoorDialog from './components/DoorDialog';
import DrainEditDialog from './components/DrainEditDialog';
import MiddleWallEditDialog from './components/MiddleWallEditDialog';
import HariEditDialog from './components/HariEditDialog';
import PanelEditDialog from './components/PanelEditDialog';
import CeilingPanelEditDialog from './components/CeilingPanelEditDialog';
import DrawingInfoPanel from './components/DrawingInfoPanel';
import HariTest from './components/HariTest';
import HariMiddleTest from './components/HariMiddleTest';
import HariClockwiseTest from './components/HariClockwiseTest';
import { EstimateForm, PanelConfig, Panel, Door, Drain, MiddleWall, Hari, HariCorner, ToolType, DrawingInfo } from './types';
import { printDrawing } from './utils/printDrawing';
import { calculateAllWalls } from './utils/panelLayout';
import { layoutCeiling, CeilingFlow, ceilingEdgeSize } from './utils/ceilingLayout';
import { fetchPanelConfig, saveDrawing, loadDrawing, errMsg, SavePanelRow, pendingCount, uploadPlanImage } from './utils/api';
import { useAuth } from './components/AuthGate';
import { LoadDialog, PasswordDialog } from './components/DrawingFileDialogs';
import { CompanyAdminDialog, StaffDialog, InquiryDialog } from './components/AdminDialogs';
import WorkBoard from './components/WorkBoard';
import ProductionBoard from './components/ProductionBoard';
import BoardDialog from './components/BoardDialog';
import { buildPlan3D } from './utils/plan3d';
import { WorkOrderDialog, PanelScan } from './components/WorkScreens';
import { QuoteDialog } from './components/QuoteDialog';
import { checkPlan, newIssuesFor, PlanInput, PlanIssue } from './utils/planCheck';
import { ExistingWallDialog, EdgeInfo } from './components/ExistingWallDialog';
import { BizDialog } from './components/BizDialogs';
import HomeBoard from './components/HomeBoard';
import { applyMaterialSpec, getMaterialSpec } from './utils/materialSpecs';
import { buildSheet, printProductionSheet } from './utils/productionSheet';
import { numberPanels } from './utils/panelNumbers';
import { labelItems, printLabels } from './utils/labelPrint';
import { rectWithNotches, harisToNotches, layoutPolygon, partitionSegments, layoutPartitions, Flow, PolyEdits, PieceInfo, Notch, Opening, Partition } from './engine/polyEngine';
import ShapeEditor, { validateCuts } from './components/ShapeEditor';
import { defaultRule, SegRule } from './utils/segmentFill';
import PolyPanelDialog from './components/PolyPanelDialog';
import { SegmentFillDialog } from './components/dialogKit';
import { mainHarisToEngine, doorsToOpenings } from './engine/polyRender';
import { sideFaces, checkDoor, nearestValidFrom, fullPanelTotals, doorFrom, doorLen, doorOffset, Range, DoorCheck } from './utils/doorPlacement';
import type { DoorGuide } from './components/DrawingCanvas';
import './App.css';

// 3D 보기는 열 때만 내려받음 (three.js 약 600KB)
const Plan3D = React.lazy(() => import('./components/Plan3D'));

const DEFAULT_CONFIG: PanelConfig = {
  CONFIG_CD: 'DEFAULT', CONFIG_NM: '기본 설정',
  DEFAULT_SIZE: 900, MIN_SIZE: 300, MAX_SIZE: 900, PANEL_SPACE: 4,
  PANEL_DEPTH_1: 75, PANEL_DEPTH_2: 100,
  DOOR_WIDTH: 800, DOOR_LEFT_PANEL: 150, DOOR_RIGHT_PANEL: 150,
};

const fullTotalsLabel = (n: number) => `900×${n} 맞춤`;

type Walls = { left: Panel[]; right: Panel[]; top: Panel[]; bottom: Panel[] };
const emptyWalls: Walls = { left: [], right: [], top: [], bottom: [] };

function App() {
  const { user, logout, toSignup, toLogin } = useAuth();
  const isGuest = user.role === 'GUEST'; // 로그인 없이 체험 — 저장·작업 관리 없음
  const [form, setForm] = useState<EstimateForm>({
    HORIZONTAL: 5000, VERTICAL: 3000, HEIGHT: 2400,
    PANEL_THICK: 100, CEILING_YN: 'Y', CEILING_HV: 'H', FLOOR_YN: 'N',
    MATERIAL: 'SP', WALL_HEIGHT: 2400,
  });
  // 천장: 분할 규칙(없으면 기본) + 결합 흐름 → 패널은 계산값
  const [ceilingRule, setCeilingRule] = useState<SegRule | null>(null);
  const [ceilingPreview, setCeilingPreview] = useState<SegRule | null | undefined>(undefined); // undefined = 미리보기 없음
  const [ceilingFlow, setCeilingFlow] = useState<CeilingFlow>('lo');
  const [ceilingSeg, setCeilingSeg] = useState(false);
  const [ceilingEdgeOn, setCeilingEdgeOn] = useState(true); // 첫 패널(벽 위) 두께별 사이즈 사용
  const [baseConfig, setBaseConfig] = useState<PanelConfig>(DEFAULT_CONFIG);
  // 기본 설정(DB) + 재질 규격(표준 폭·반절·최소) — 이하 모든 계산은 이 config 사용
  const config = useMemo(() => applyMaterialSpec(baseConfig, form.MATERIAL), [baseConfig, form.MATERIAL]);
  const [walls, setWalls] = useState<Walls>(emptyWalls);
  const [doors, setDoors] = useState<Door[]>([]);
  // 패드·폰(가로 1280 미만 — 아이패드 가로 포함)은 사이드바 접고 시작 — 도면을 크게
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1280);
  const [showHariTest, setShowHariTest] = useState(() => new URLSearchParams(window.location.search).get('view') === 'hari');
  const [drains, setDrains] = useState<Drain[]>([]);
  const [middleWalls, setMiddleWalls] = useState<MiddleWall[]>([]);
  const [middleWallDir, setMiddleWallDir] = useState<'horizontal' | 'vertical'>('vertical');
  // 저장: 현재 견적번호, 마지막 저장 내용(변경 여부 판단), 알림, 창
  const [estiNo, setEstiNo] = useState<string | null>(null);
  const [savedSig, setSavedSig] = useState<string>('');
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [fileDialog, setFileDialog] = useState<'load' | 'password' | 'admin' | 'staff' | 'inquiry' | 'quote' | 'biz' | null>(null);
  // 최고관리자: 가입 승인 대기 건수 (1분마다)
  const [pending, setPending] = useState(0);
  const [newInq, setNewInq] = useState(0); // 새 1:1 문의
  useEffect(() => {
    if (user.role !== 'SUPER') return;
    const tick = () => pendingCount().then(r => { setPending(r.data.count); setNewInq(r.data.inquiries); }).catch(() => {});
    tick();
    const t = window.setInterval(tick, 60 * 1000);
    return () => window.clearInterval(t);
  }, [user.role]);
  const [saving, setSaving] = useState(false);
  const [showNumbers, setShowNumbers] = useState(false); // 도면에 패널 번호 보기
  // 작업 현황(견적번호 = 처음 고를 견적, true = 목록만) · 작업지시 창 · QR로 연 패널(?p=견적번호:패널번호)
  const [board, setBoard] = useState<string | true | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [prod, setProd] = useState<string | true | null>(null); // 생산 화면 (견적번호 = 처음 고를 작업)
  const [show3d, setShow3d] = useState(false);
  const [board2, setBoard2] = useState<{ post?: number } | null>(null); // 게시판 (글 번호 = 그 글 바로)
  // 업무 홈 (로그인하면 처음 보임 — QR로 들어온 경우 빼고) · 수기 견적(도면 없음) 견적서
  const [home, setHome] = useState(() => user.role !== 'GUEST' && !new URLSearchParams(window.location.search).get('p'));
  const [handQuote, setHandQuote] = useState<string | null>(null);
  const [scanCode, setScanCode] = useState<string | null>(() => new URLSearchParams(window.location.search).get('p'));
  const closeScan = () => { setScanCode(null); window.history.replaceState(null, '', window.location.pathname); };
  const [middleWallEdit, setMiddleWallEdit] = useState<MiddleWall | null>(null);
  const [haris, setHaris] = useState<Hari[]>([]);
  const [hariEdit, setHariEdit] = useState<Hari | null>(null);
  const [generated, setGenerated] = useState(false);
  const [flow, setFlow] = useState<Flow>('ccw'); // 패널 흐름 방향 (반시계 기본)
  // 다각형 엔진 편집값 (확정) / 미리보기 / 선택 패널 / 구간 채우기 대상 조각
  const [polyEdits, setPolyEdits] = useState<PolyEdits>({});
  const [previewEdits, setPreviewEdits] = useState<PolyEdits | null>(null);
  const [selectedPolyId, setSelectedPolyId] = useState<string | null>(null);
  const [segPieceKey, setSegPieceKey] = useState<string | null>(null);
  // 평면 모양(잘라낸 부분) + 기존 벽(패널 없는 변)
  const [cuts, setCuts] = useState<Notch[]>([]);
  const [existingWalls, setExistingWalls] = useState<Opening[]>([]);
  const [existingEdit, setExistingEdit] = useState<EdgeInfo | null>(null); // 기존 벽 구간 창
  const [activeTool, setActiveTool] = useState<ToolType>('select');
  const [drawingInfo, setDrawingInfo] = useState<DrawingInfo>({
    siteName: '', panelType: 'SP 100T', panelSpec: '',
    grooveInfo: '', drawingNo: '', date: new Date().toISOString().slice(0, 10),
    drawnBy: '', memo: '', material: '', wallHeight: '', contact: '', shipDate: '',
  });

  // 문 수정 다이얼로그 (신규 추가 / 기존 수정 공용)
  const [doorDialog, setDoorDialog] = useState<{
    door: Door;
    isNew: boolean;
  } | null>(null);
  // 문 미리보기 (끌기·문 창 편집 중 — 확정 전 도면에 바로 반영) + 끌기 안내선
  const [doorPreview, setDoorPreview] = useState<Door | null>(null);
  const [doorGuide, setDoorGuide] = useState<DoorGuide | null>(null);
  // 인쇄: 화면 캔버스가 같은 그림을 원하는 크기로 그려 줌
  const sceneExportRef = useRef<((w: number, h: number) => HTMLCanvasElement) | null>(null);

  // 배수구 수정 다이얼로그
  const [drainEditDialog, setDrainEditDialog] = useState<Drain | null>(null);

  // 패널 수정 다이얼로그
  const [panelEdit, setPanelEdit] = useState<{
    wallSide: 'left' | 'right' | 'top' | 'bottom';
    index: number;
    panel: Panel;
  } | null>(null);

  // 천장 패널 수정 다이얼로그
  const [ceilingEdit, setCeilingEdit] = useState<{ index: number } | null>(null);

  useEffect(() => {
    if (isGuest || user.role === 'PROD') return; // 체험은 기본 설정으로 · 생산 직원은 도면 없음
    fetchPanelConfig()
      .then(res => { if (res.data?.length > 0) setBaseConfig(res.data[0]); })
      .catch(() => console.log('패널 설정 기본값 사용'));
  }, [isGuest, user.role]);

  // ── 배치 검사: 만들 수 없는 배치 → 경고 목록 / 새로 놓거나 고칠 때 새 문제가 생기면 막음 ──
  const planInput = useMemo<PlanInput>(() => ({ H: form.HORIZONTAL, V: form.VERTICAL, PT: form.PANEL_THICK, min: config.SPLIT_MIN_SIZE ?? 200,
    cuts, haris, doors, middleWalls, drains, existing: existingWalls }),
  [form.HORIZONTAL, form.VERTICAL, form.PANEL_THICK, config.SPLIT_MIN_SIZE, cuts, haris, doors, middleWalls, drains, existingWalls]);
  const issues = useMemo(() => (generated ? checkPlan(planInput) : []), [generated, planInput]);
  const [showIssues, setShowIssues] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // 머리말 회사 메뉴
  const warnTimer = useRef<number | undefined>(undefined);
  /** patch로 바꿔도 되는지 (quiet = 끌기 중: 알림 없이 마지막 좋은 위치에 멈춤) */
  const allowed = useCallback((patch: Partial<PlanInput>, id: string, quiet = false) => {
    const bad = newIssuesFor(issues, checkPlan({ ...planInput, ...patch }), id);
    if (bad.length && !quiet) {
      setNotice({ ok: false, text: `놓을 수 없습니다 — ${bad[0].msg}` });
      window.clearTimeout(warnTimer.current);
      warnTimer.current = window.setTimeout(() => setNotice(null), 4500);
    }
    return !bad.length;
  }, [issues, planInput]);

  // 패널 재계산 (문 포함)
  const recalcWalls = useCallback((doorList: Door[], hariList?: Hari[]) => {
    const result = calculateAllWalls(form.HORIZONTAL, form.VERTICAL, config, doorList, form.PANEL_THICK, hariList ?? haris);
setWalls(result);
  }, [form, config, haris]);

  const handleGenerate = useCallback(() => {
    if (form.HORIZONTAL <= 0 || form.VERTICAL <= 0) {
      alert('가로/세로 값을 입력해주세요.');
      return;
    }
    const result = calculateAllWalls(form.HORIZONTAL, form.VERTICAL, config, [], form.PANEL_THICK);
    setWalls(result);
    setDoors([]);
    setDrains([]);
    setMiddleWalls([]);
    setHaris([]);
    setPolyEdits({}); setPreviewEdits(null); setSelectedPolyId(null); setSegPieceKey(null);
    setCeilingRule(null); setCeilingPreview(undefined); setCeilingEdit(null);
    setEstiNo(null); setSavedSig('');
    setGenerated(true);
    setActiveTool('select');
  }, [form, config]);

  // 기본 문 생성 헬퍼
  // clickPos: 벽 클릭 좌표 (전체 도면 기준)
  // 벽별 패널 시작 오프셋 고려:
  //   top: panelThick부터 시작 → position = clickPos - panelThick
  //   bottom: 0부터 시작 → position = clickPos
  //   left: 0부터 시작 → position = clickPos
  //   right: panelThick부터 시작 → position = clickPos - panelThick
  const makeDefaultDoor = useCallback((wallSide: 'left' | 'right' | 'top' | 'bottom', clickPos: number): Door => {
    const lp = config.DOOR_LEFT_PANEL;   // 150
    const rp = config.DOOR_RIGHT_PANEL;  // 150
    const dw = config.DOOR_WIDTH;        // 800
    const total = lp + dw + rp;          // 1100
    const offset = (wallSide === 'top' || wallSide === 'right') ? form.PANEL_THICK : 0;
    const localPos = Math.max(0, Math.round(clickPos - offset - total / 2));
    return {
      id: `door_${Date.now()}`,
      wallSide,
      position: localPos,
      width: dw, height: 1800,
      leftPanel: lp, rightPanel: rp,
      grooveDir: 'IN', openDir: 'right',
      doorType: 'single-right',
    };
  }, [config, form.PANEL_THICK]);

  // 배치된 문 클릭 → 수정 팝업
  const handleDoorClick = useCallback((doorId: string) => {
    const door = doors.find(d => d.id === doorId);
    if (door) setDoorDialog({ door, isNew: false });
  }, [doors]);

  // 바닥 클릭 → 기본값으로 즉시 배수구 추가
  const handleFloorClick = useCallback((x: number, y: number) => {
    const newDrain: Drain = {
      id: `drain_${Date.now()}`,
      x: Math.round(x),
      y: Math.round(y),
      size: 150,
      direction: 'right',
    };
    if (!allowed({ drains: [...drains, newDrain] }, newDrain.id)) return;
    setDrains(prev => [...prev, newDrain]);
    setActiveTool('select');
  }, [drains, allowed]);

  // 문 수정 확정 → 패널 재계산
  const handleDoorConfirm = useCallback((updatedDoor: Door) => {
    const newDoors = doorDialog?.isNew
      ? [...doors, updatedDoor]
      : doors.map(d => d.id === updatedDoor.id ? updatedDoor : d);
    if (!allowed({ doors: newDoors }, updatedDoor.id)) return;
    setDoors(newDoors);
    recalcWalls(newDoors);
    setDoorDialog(null);
    setDoorPreview(null);
  }, [doors, recalcWalls, doorDialog, allowed]);

  // 문 삭제 → 패널 재계산 (문 삽입 전 상태로 복원)
  const handleDoorDelete = useCallback((doorId: string) => {
    const newDoors = doors.filter(d => d.id !== doorId);
    setDoors(newDoors);
    recalcWalls(newDoors);
    setDoorDialog(null);
    setDoorPreview(null);
  }, [doors, recalcWalls]);


  // 문 삭제 → 패널 재계산
  const handleDeleteDoor = useCallback((id: string) => {
    const newDoors = doors.filter(d => d.id !== id);
    setDoors(newDoors);
    recalcWalls(newDoors);
  }, [doors, recalcWalls]);

  const handleDeleteDrain = useCallback((id: string) => {
    setDrains(prev => prev.filter(d => d.id !== id));
  }, []);

  // 배수구 드래그로 위치 변경
  const handleDrainMove = useCallback((id: string, x: number, y: number) => {
    if (!allowed({ drains: drains.map(d => d.id === id ? { ...d, x, y } : d) }, id, true)) return;
    setDrains(prev => prev.map(d => d.id === id ? { ...d, x, y } : d));
  }, [drains, allowed]);

  // 배수구 클릭 → 수정 팝업
  const handleDrainClick = useCallback((drainId: string) => {
    const drain = drains.find(d => d.id === drainId);
    if (drain) setDrainEditDialog(drain);
  }, [drains]);

  // 배수구 수정 확정
  const handleDrainUpdate = useCallback((updated: Drain) => {
    if (!allowed({ drains: drains.map(d => d.id === updated.id ? updated : d) }, updated.id)) return;
    setDrains(prev => prev.map(d => d.id === updated.id ? updated : d));
    setDrainEditDialog(null);
  }, [drains, allowed]);

  // 배수구 삭제 (수정 팝업에서)
  const handleDrainDeleteFromEdit = useCallback((id: string) => {
    setDrains(prev => prev.filter(d => d.id !== id));
    setDrainEditDialog(null);
  }, []);

  // 칸막이 배치 (도면 클릭)
  const handleMiddleWallPlace = useCallback((x: number, y: number) => {
    const pt = form.PANEL_THICK;
    const position = middleWallDir === 'vertical'
      ? Math.round(Math.max(0, x - pt))
      : Math.round(Math.max(0, y - pt));
    const length = middleWallDir === 'vertical'
      ? form.VERTICAL - pt * 2
      : form.HORIZONTAL - pt * 2;
    const newMW: MiddleWall = {
      id: `mw_${Date.now()}`,
      direction: middleWallDir,
      position,
      length,
      panelThick: pt,
      panelVariant: 1,
      anchor: middleWallDir === 'vertical' ? y : x,
    };
    if (!allowed({ middleWalls: [...middleWalls, newMW] }, newMW.id)) return;
    setMiddleWalls(prev => [...prev, newMW]);
    setActiveTool('select');
  }, [middleWallDir, form, middleWalls, allowed]);

  // 칸막이 드래그 이동
  const handleMiddleWallMove = useCallback((id: string, position: number) => {
    if (!allowed({ middleWalls: middleWalls.map(w => w.id === id ? { ...w, position } : w) }, id, true)) return;
    setMiddleWalls(prev => prev.map(w => w.id === id ? { ...w, position } : w));
  }, [middleWalls, allowed]);

  // 칸막이 클릭 → 수정 팝업
  const handleMiddleWallClick = useCallback((id: string) => {
    const mw = middleWalls.find(w => w.id === id);
    if (mw) setMiddleWallEdit(mw);
  }, [middleWalls]);

  // 칸막이 수정 확정
  const handleMiddleWallUpdate = useCallback((updated: MiddleWall) => {
    if (!allowed({ middleWalls: middleWalls.map(w => w.id === updated.id ? updated : w) }, updated.id)) return;
    setMiddleWalls(prev => prev.map(w => w.id === updated.id ? updated : w));
    setMiddleWallEdit(null);
  }, [middleWalls, allowed]);

  // 칸막이 삭제
  const handleMiddleWallDelete = useCallback((id: string) => {
    setMiddleWalls(prev => prev.filter(w => w.id !== id));
    setMiddleWallEdit(null);
  }, []);

  // 하리 배치 (도면 클릭)
  const handleHariPlace = useCallback((x: number, y: number) => {
    const H = form.HORIZONTAL, V = form.VERTICAL, pt = form.PANEL_THICK;
    const threshold = Math.min(H, V) * 0.35;
    const distTL = Math.hypot(x, y);
    const distTR = Math.hypot(x - H, y);
    const distBL = Math.hypot(x, y - V);
    const distBR = Math.hypot(x - H, y - V);
    const minDist = Math.min(distTL, distTR, distBL, distBR);
    const defaultW = 500, defaultH = 500;

    let newHari: Hari;
    if (minDist < threshold) {
      const corner: HariCorner = minDist === distTL ? 'topLeft'
        : minDist === distTR ? 'topRight'
        : minDist === distBL ? 'bottomLeft' : 'bottomRight';
      newHari = { id: `hari_${Date.now()}`, hariType: 'corner', corner, width: defaultW, height: defaultH };
    } else {
      // 가장 가까운 벽 판단
      const dTop = y, dBottom = V - y, dLeft = x, dRight = H - x;
      const minW = Math.min(dTop, dBottom, dLeft, dRight);
      const ws = minW === dTop ? 'top' : minW === dBottom ? 'bottom' : minW === dLeft ? 'left' : 'right';
      const isHoriz = ws === 'top' || ws === 'bottom';
      const posOffset = (ws === 'top' || ws === 'right') ? pt : 0;
      const pos = isHoriz ? Math.max(0, Math.round(x - posOffset - defaultW / 2)) : Math.max(0, Math.round(y - posOffset - defaultW / 2));
      newHari = { id: `hari_${Date.now()}`, hariType: 'middle', wallSide: ws, position: pos, width: defaultW, height: defaultH };
    }
    const newHaris = [...haris, newHari];
    if (!allowed({ haris: newHaris }, newHari.id)) return;
    setHaris(newHaris);
    recalcWalls(doors, newHaris);
    setHariEdit(newHari);
    setActiveTool('select');
  }, [haris, doors, form, recalcWalls, allowed]);

  const handleHariUpdate = useCallback((updated: Hari) => {
    const newHaris = haris.map(h => h.id === updated.id ? updated : h);
    if (!allowed({ haris: newHaris }, updated.id)) return;
    setHaris(newHaris);
    recalcWalls(doors, newHaris);
    setHariEdit(null);
  }, [haris, doors, recalcWalls, allowed]);

  const handleHariDelete = useCallback((id: string) => {
    const newHaris = haris.filter(h => h.id !== id);
    setHaris(newHaris);
    recalcWalls(doors, newHaris);
    setHariEdit(null);
  }, [haris, doors, recalcWalls]);


  // 패널 클릭 → 수정 팝업
  const handlePanelClick = useCallback((wallSide: 'left' | 'right' | 'top' | 'bottom', panelIndex: number) => {
    const panel = walls[wallSide][panelIndex];
    if (panel) {
      setPanelEdit({ wallSide, index: panelIndex, panel });
    }
  }, [walls]);

  // 패널 수정 확정 → 크기 변경 시 나머지 재계산 + 인접 패널 음양수 자동 정합
  const handlePanelUpdate = useCallback((updated: Panel) => {
    if (!panelEdit) return;
    const { wallSide, index } = panelEdit;
    const oldPanels = walls[wallSide];
    const sizeChanged = updated.size !== oldPanels[index].size;

    let newArr: Panel[];

    if (sizeChanged) {
      const isVert = wallSide === 'left' || wallSide === 'right';
      const wallLen = isVert ? form.VERTICAL - form.PANEL_THICK : form.HORIZONTAL - form.PANEL_THICK;
      const fixedSize = updated.size;
      const otherTotal = wallLen - fixedSize - config.PANEL_SPACE * (oldPanels.length - 1);
      const otherSizes = oldPanels.filter((_, i) => i !== index).map(p => p.size);
      const otherSum = otherSizes.reduce((a, b) => a + b, 0);
      newArr = oldPanels.map((p, i) => {
        if (i === index) return { ...updated };
        const ratio = otherSum > 0 ? p.size / otherSum : 1 / otherSizes.length;
        return { ...p, size: Math.round(otherTotal * ratio), isCustom: true };
      });
    } else {
      // 크기 변경 없음 → 타입/그루브만 교체
      newArr = oldPanels.map((p, i) => i === index ? { ...updated } : { ...p });
    }

    // 인접 패널 데이터 정합 (코너 제외)
    // 회전 후 결합부 방향:
    //   top(0도), left(-90도): 이전rt ↔ 현재lt  → 이전rt = 현재lt의 반대
    //   bottom(-180도), right(-270도): 이전lt ↔ 현재rt → 이전lt = 현재rt의 반대
    const isReversed = wallSide === 'bottom' || wallSide === 'left';

    if (!isReversed) {
      // top / left
      if (index > 0 && newArr[index - 1].rightType !== 'corner') {
        newArr[index - 1] = {
          ...newArr[index - 1],
          rightType: newArr[index].leftType === 'yangsu' ? 'eumsu' : 'yangsu',
        };
      }
      if (index < newArr.length - 1 && newArr[index + 1].leftType !== 'corner') {
        newArr[index + 1] = {
          ...newArr[index + 1],
          leftType: newArr[index].rightType === 'yangsu' ? 'eumsu' : 'yangsu',
        };
      }
    } else {
      // bottom / right: lt ↔ rt 역할 반대
      if (index > 0 && newArr[index - 1].leftType !== 'corner') {
        newArr[index - 1] = {
          ...newArr[index - 1],
          leftType: newArr[index].rightType === 'yangsu' ? 'eumsu' : 'yangsu',
        };
      }
      if (index < newArr.length - 1 && newArr[index + 1].rightType !== 'corner') {
        newArr[index + 1] = {
          ...newArr[index + 1],
          rightType: newArr[index].leftType === 'yangsu' ? 'eumsu' : 'yangsu',
        };
      }
    }

    setWalls(prev => ({ ...prev, [wallSide]: newArr }));
    setPanelEdit(null);
  }, [panelEdit, walls, form, config]);

  // 벽면 패널 배치 가능 길이 (panelThick 제외, calculateAllWalls와 동일)
  const getWallLength = (side: 'left' | 'right' | 'top' | 'bottom') => {
    return (side === 'top' || side === 'bottom')
      ? form.HORIZONTAL - form.PANEL_THICK
      : form.VERTICAL - form.PANEL_THICK;
  };

  const sideNames: Record<string, string> = {
    top: '상단', bottom: '하단', left: '좌측', right: '우측'
  };

  // 테스트 모드 — 버튼으로 토글
  // 다각형 엔진: 외곽(가로×세로 − 하리 홈) → 패널 (문은 외벽 개구부로 비움)
  const fillSpec = useMemo(() => ({
    min: config.SPLIT_MIN_SIZE ?? 200, max: config.MAX_SIZE, standard: config.DEFAULT_SIZE,
    half: config.HALF_SIZE ?? 441, space: config.PANEL_SPACE,
  }), [config]);
  // 천장 패널 (미리보기 규칙이 있으면 그것으로)
  const ceilingEdge = ceilingEdgeOn ? ceilingEdgeSize(form.PANEL_THICK) : null;
  const ceiling = useMemo(() => (generated && form.CEILING_YN === 'Y'
    ? layoutCeiling(form.HORIZONTAL, form.VERTICAL, form.CEILING_HV as 'H' | 'V', ceilingFlow,
      ceilingPreview !== undefined ? ceilingPreview : ceilingRule, fillSpec, ceilingEdge)
    : null), [generated, form.CEILING_YN, form.HORIZONTAL, form.VERTICAL, form.CEILING_HV, ceilingFlow, ceilingPreview, ceilingRule, fillSpec, ceilingEdge]);
  const ceilingPanels = useMemo(() => ceiling?.panels ?? [], [ceiling]);
  // 수정 창 기준 = 확정 규칙 결과 (미리보기 아님)
  const ceilingBase = useMemo(() => (ceilingPreview === undefined ? ceiling
    : generated && form.CEILING_YN === 'Y' ? layoutCeiling(form.HORIZONTAL, form.VERTICAL, form.CEILING_HV as 'H' | 'V', ceilingFlow, ceilingRule, fillSpec, ceilingEdge) : null),
  [ceilingPreview, ceiling, generated, form.CEILING_YN, form.HORIZONTAL, form.VERTICAL, form.CEILING_HV, ceilingFlow, ceilingRule, fillSpec, ceilingEdge]);
  const handleCeilingPanelClick = useCallback((index: number) => { setCeilingPreview(undefined); setCeilingSeg(false); setCeilingEdit({ index }); }, []);
  const closeCeiling = () => { setCeilingEdit(null); setCeilingSeg(false); setCeilingPreview(undefined); };
  // 외곽 다각형: 잘라낸 부분이 잘못되면(범위·겹침) 직사각형으로 그림
  const outline = useMemo(() => {
    const H = form.HORIZONTAL, V = form.VERTICAL, PT = form.PANEL_THICK;
    const validCuts = validateCuts(H, V, cuts).length ? [] : cuts;
    return rectWithNotches(H, V, [...validCuts, ...harisToNotches(mainHarisToEngine(haris, H, V, PT), PT)]);
  }, [form.HORIZONTAL, form.VERTICAL, form.PANEL_THICK, cuts, haris]);
  // 화면에 쓰는 문 목록 (미리보기 중인 문은 미리보기 위치로)
  const effDoors = useMemo(() => (doorPreview ? doors.map(d => (d.id === doorPreview.id ? doorPreview : d)) : doors), [doors, doorPreview]);
  // 편집값 + 문 목록으로 배치
  const layoutCore = useCallback((edits: PolyEdits, doors: Door[]) => {
    const PT = form.PANEL_THICK;
    const pieces: Record<string, PieceInfo> = {};
    const doorEndMap = new Map<Opening, { lo: 'yangsu' | 'eumsu'; hi: 'yangsu' | 'eumsu' }>();
    const panels = layoutPolygon(outline, PT, flow, fillSpec, [...doorsToOpenings(doors, PT, fillSpec.space), ...existingWalls], edits, pieces, doorEndMap);
    const doorEnds: Record<string, { lo: 'yangsu' | 'eumsu'; hi: 'yangsu' | 'eumsu' }> = {};
    doorEndMap.forEach((v, o) => { if (o.id) doorEnds[o.id] = v; });
    // 칸막이: 메인 위치(안쪽 면 기준) → 중심선 좌표, 양 끝은 벽 안쪽 면·기존 벽·먼저 놓인 칸막이 면
    const parts: Partition[] = middleWalls.map(mw => ({
      id: mw.id, dir: mw.direction === 'vertical' ? 'v' : 'h', pos: PT + mw.position, anchor: mw.anchor, thick: mw.panelThick,
    }));
    const partPanels = layoutPartitions(partitionSegments(outline, PT, parts, existingWalls), fillSpec, edits, pieces);
    return { outline: outline.pts, panels: [...panels, ...partPanels], pieces, doorEnds };
  }, [form.PANEL_THICK, outline, flow, fillSpec, existingWalls, middleWalls]);
  // 확정 편집값으로 배치 (편집 창 기준) / 미리보기 편집값으로 배치 (화면 표시)
  const layoutWith = useCallback((edits: PolyEdits) => layoutCore(edits, effDoors), [layoutCore, effDoors]);

  // ── 문 배치 ──
  // 면 = 외곽 경계선 위 변의 안쪽 면 구간, 다른 문은 장애물. 양옆 남는 패널은 최소(200) 이상
  const doorSpace = useCallback((door: Door, list: Door[]) => {
    const PT = form.PANEL_THICK;
    const faces = sideFaces(outline.pts, door.wallSide, form.HORIZONTAL, form.VERTICAL, PT);
    const others: Range[] = list.filter(d => d.id !== door.id && d.wallSide === door.wallSide)
      .map(d => ({ lo: doorFrom(d, PT), hi: doorFrom(d, PT) + doorLen(d) }));
    return { faces, others };
  }, [outline, form.HORIZONTAL, form.VERTICAL, form.PANEL_THICK]);
  const checkDoorAt = useCallback((door: Door): DoorCheck => {
    const { faces, others } = doorSpace(door, doors);
    return checkDoor(door.wallSide, doorFrom(door, form.PANEL_THICK), doorLen(door), faces, others, fillSpec.min);
  }, [doorSpace, doors, form.PANEL_THICK, fillSpec.min]);
  const fullTotals = useMemo(() => fullPanelTotals(fillSpec, Math.max(form.HORIZONTAL, form.VERTICAL)), [fillSpec, form.HORIZONTAL, form.VERTICAL]);

  // 벽 클릭 → 기본 문을 클릭 위치에 (놓을 수 없으면 가장 가까운 놓을 수 있는 자리로)
  const handleWallClick = useCallback((wallSide: 'left' | 'right' | 'top' | 'bottom', position: number) => {
    const newDoor = makeDefaultDoor(wallSide, position);
    const PT = form.PANEL_THICK, off = doorOffset(wallSide, PT);
    const { faces, others } = doorSpace(newDoor, doors);
    const from = nearestValidFrom(off + newDoor.position, doorLen(newDoor), faces, others, fillSpec.min);
    if (from === null) { alert('이 벽에는 문을 놓을 공간이 없습니다 (코너·하리·다른 문 사이가 좁음)'); return; }
    const newDoors = [...doors, { ...newDoor, position: Math.round(from - off) }];
    setDoors(newDoors);
    recalcWalls(newDoors);
    setActiveTool('select');
  }, [doors, recalcWalls, makeDefaultDoor, doorSpace, form.PANEL_THICK, fillSpec.min]);

  // 문 끌기: 놓을 수 있는 자리로 붙이고, 옆 조각이 900만으로 딱 떨어지는 자리에 자석처럼 붙음
  const handleDoorDrag = useCallback((id: string, rawPos: number, phase: 'move' | 'end', snapMM: number) => {
    const door = doors.find(d => d.id === id);
    if (!door) return;
    const PT = form.PANEL_THICK, side = door.wallSide, off = doorOffset(side, PT), len = doorLen(door);
    const { faces, others } = doorSpace(door, doors);
    const raw = off + rawPos;
    let from = nearestValidFrom(Math.round(raw / 10) * 10, len, faces, others, fillSpec.min);
    if (from === null) return;
    // 옆 조각 길이: 문 양옆 패널 조각의 전체 길이 (조각 시작·끝은 문 위치와 무관)
    const at = (f: number) => layoutCore(polyEdits, doors.map(d => (d.id === id ? { ...d, position: f - off } : d)));
    const pieceTotals = (f: number) => {
      const res = at(f), horiz = side === 'top' || side === 'bottom';
      const lineC = side === 'top' || side === 'left' ? PT / 2 : (side === 'bottom' ? form.VERTICAL : form.HORIZONTAL) - PT / 2;
      const onLine = res.panels.filter(p => (horiz ? Math.abs(p.a.y - lineC) < 1 && Math.abs(p.b.y - lineC) < 1 : Math.abs(p.a.x - lineC) < 1 && Math.abs(p.b.x - lineC) < 1));
      const ax = (q: { x: number; y: number }) => (horiz ? q.x : q.y);
      const lo = onLine.find(p => Math.abs(Math.max(ax(p.a), ax(p.b)) - f) <= 6);
      const hi = onLine.find(p => Math.abs(Math.min(ax(p.a), ax(p.b)) - (f + len)) <= 6);
      return { lo: lo ? res.pieces[lo.pieceKey]?.total : undefined, hi: hi ? res.pieces[hi.pieceKey]?.total : undefined };
    };
    const t0 = pieceTotals(from);
    let snap: string | undefined;
    let best = snapMM + 1;
    const loName = side === 'top' || side === 'bottom' ? '왼쪽' : '위쪽', hiName = side === 'top' || side === 'bottom' ? '오른쪽' : '아래쪽';
    fullTotals.forEach(({ total, count }) => {
      const cands: [number, string][] = [];
      if (t0.lo !== undefined) cands.push([from! - t0.lo + total, `${loName} ${fullTotalsLabel(count)}`]);
      if (t0.hi !== undefined) cands.push([from! + len + t0.hi - total - len, `${hiName} ${fullTotalsLabel(count)}`]);
      cands.forEach(([c, label]) => {
        const d = Math.abs(c - raw);
        if (d < best && checkDoor(side, c, len, faces, others, fillSpec.min).ok) { best = d; snap = label; from = c; }
      });
    });
    const position = Math.round(from - off);
    if (phase === 'end') {
      const newDoors = doors.map(d => (d.id === id ? { ...d, position } : d));
      setDoors(newDoors);
      recalcWalls(newDoors);
      setDoorPreview(null);
      setDoorGuide(null);
      return;
    }
    const chk = checkDoor(side, from, len, faces, others, fillSpec.min);
    const t = snap ? pieceTotals(from) : t0;
    setDoorPreview({ ...door, position });
    setDoorGuide({ id, side, from, to: from + len, slot: chk.slot ?? { lo: from, hi: from + len }, snap, pieceLo: t.lo, pieceHi: t.hi });
  }, [doors, doorSpace, layoutCore, polyEdits, fullTotals, fillSpec.min, form.PANEL_THICK, form.HORIZONTAL, form.VERTICAL, recalcWalls]);
  const polyBase = useMemo(() => (generated ? layoutWith(polyEdits) : null), [generated, layoutWith, polyEdits]);
  // 화면·저장·작업지시용 문제 목록 = 배치 검사 + 패널 조각 오류(최소보다 짧은 조각 등)
  const shownIssues = useMemo<PlanIssue[]>(() => [...issues,
    ...Object.values(polyBase?.pieces ?? {}).filter(pc => pc.error).map(pc => ({ target: 'wall' as const, msg: `패널 조각 ${pc.total}mm: ${pc.error}` }))],
  [issues, polyBase]);
  const poly = useMemo(() => (generated && previewEdits ? layoutWith(previewEdits) : polyBase), [generated, previewEdits, layoutWith, polyBase]);
  // 3D 보기 모형 — 2D·재단표와 같은 패널 계산 결과로 (열려 있을 때만 만듦)
  const model3d = useMemo(() => {
    if (!show3d || !poly) return null;
    const PT = form.PANEL_THICK, H = form.HORIZONTAL, V = form.VERTICAL;
    return buildPlan3D({
      outline: outline.pts, panels: poly.panels, PT, wallHeight: form.WALL_HEIGHT || form.HEIGHT, std: fillSpec.standard, half: fillSpec.half,
      ceiling: ceilingPanels, ceilingDir: form.CEILING_HV === 'V' ? 'V' : 'H', floor: form.FLOOR_YN === 'Y',
      doors: effDoors, doorOpenings: doorsToOpenings(effDoors, PT, fillSpec.space), existing: existingWalls,
      drains, H, V,
    });
  }, [show3d, poly, outline, form.PANEL_THICK, form.HORIZONTAL, form.VERTICAL, form.WALL_HEIGHT, form.HEIGHT, form.CEILING_HV, form.FLOOR_YN,
    fillSpec, ceilingPanels, effDoors, existingWalls, drains]);
  // 패널 번호 (도면 표시용)
  const panelNos = useMemo(() => (poly && showNumbers ? numberPanels(poly.panels) : null), [poly, showNumbers]);
  // 재단표 행 (생산지시서·저장 공용)
  const sheetRows = useMemo(() => (poly ? buildSheet({ panels: poly.panels, ceiling: ceilingPanels, floor: form.FLOOR_YN === 'Y',
    wallHeight: form.WALL_HEIGHT, std: fillSpec.standard, half: fillSpec.half }) : []), [poly, ceilingPanels, form.FLOOR_YN, form.WALL_HEIGHT, fillSpec]);

  // ── 저장·불러오기 ──
  // 도면 전체 상태 (이것만 있으면 화면을 그대로 복원)
  const plan = useMemo(() => ({
    v: 1, form, doors, drains, middleWalls, haris, cuts, existingWalls, polyEdits, flow,
    ceilingRule, ceilingFlow, ceilingEdgeOn, drawingInfo,
  }), [form, doors, drains, middleWalls, haris, cuts, existingWalls, polyEdits, flow, ceilingRule, ceilingFlow, ceilingEdgeOn, drawingInfo]);
  const planSig = useMemo(() => JSON.stringify(plan), [plan]);
  const dirty = generated && planSig !== savedSig;
  const confirmDiscard = () => !dirty || window.confirm('저장하지 않은 변경이 있습니다. 버리고 계속할까요?');
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty && !isGuest) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, isGuest]);
  const flash = (ok: boolean, text: string) => { setNotice({ ok, text }); window.setTimeout(() => setNotice(null), 3500); };

  const handleSave = async (asNew: boolean) => {
    if (!generated || !poly) { flash(false, '먼저 도면을 생성하세요'); return; }
    if (shownIssues.length && !window.confirm(`확인 필요 ${shownIssues.length}건이 있습니다:\n\n${shownIssues.slice(0, 8).map(i => '· ' + i.msg).join('\n')}\n\n그래도 저장할까요?`)) return;
    // 이전 시스템 형식: 벽판넬 / 칸막이 / 천.바(천장+바닥) — 사이즈·철판별 수량
    const agg = new Map<string, SavePanelRow>();
    sheetRows.forEach(r => {
      const nm = r.kind === '벽' ? '벽판넬' : r.kind === '칸막이' ? '칸막이' : '천.바';
      const k = `${nm}|${r.size}|${r.steel}`;
      const row = agg.get(k) ?? { PANEL_NM: nm, PANEL_SIZE: r.size, STEEL_SIZE: r.steel, QTY: 0 };
      row.QTY += r.qty; agg.set(k, row);
    });
    setSaving(true);
    try {
      const res = await saveDrawing({
        estiNo: asNew ? null : estiNo,
        estimate: {
          MATERIAL_CD: form.MATERIAL, HORIZONTAL: form.HORIZONTAL, VERTICAL: form.VERTICAL, HEIGHT: form.HEIGHT, WALLHEIGHT: form.WALL_HEIGHT,
          CEILING_YN: form.CEILING_YN, FLOOR_YN: form.FLOOR_YN, CEILING_HV: form.CEILING_HV, KAN_QTY: middleWalls.length,
          PANEL_THICK: form.PANEL_THICK, NOTICE: drawingInfo.memo,
        },
        plan, panels: Array.from(agg.values()),
      });
      setEstiNo(res.data.estiNo); setSavedSig(planSig);
      flash(true, `${res.data.created ? '새로 저장' : '저장'}했습니다 — ${res.data.estiNo}`);
    } catch (e) { flash(false, `저장 실패: ${errMsg(e)}`); }
    finally { setSaving(false); }
  };

  // 저장된 도면 상태를 화면에 적용 (불러오기·체험 도면 이어받기 공용)
  const applyPlan = (p: any) => {
      setForm(p.form); setDoors(p.doors ?? []); setDrains(p.drains ?? []); setMiddleWalls(p.middleWalls ?? []); setHaris(p.haris ?? []);
      setCuts(p.cuts ?? []); setExistingWalls(p.existingWalls ?? []); setPolyEdits(p.polyEdits ?? {}); setFlow(p.flow ?? 'ccw');
      setCeilingRule(p.ceilingRule ?? null); setCeilingFlow(p.ceilingFlow ?? 'lo'); setCeilingEdgeOn(p.ceilingEdgeOn ?? true);
      if (p.drawingInfo) setDrawingInfo(p.drawingInfo);
      setWalls(calculateAllWalls(p.form.HORIZONTAL, p.form.VERTICAL, applyMaterialSpec(baseConfig, p.form.MATERIAL), p.doors ?? [], p.form.PANEL_THICK, p.haris ?? []));
      setPreviewEdits(null); setSelectedPolyId(null); setSegPieceKey(null); setDoorPreview(null); setDoorGuide(null);
      setCeilingPreview(undefined); setCeilingEdit(null); setDoorDialog(null);
      setGenerated(true); setActiveTool('select');
  };
  const handleLoad = async (no: string): Promise<'drawing' | 'hand' | null> => {
    try {
      const { data } = await loadDrawing(no);
      const p = data.plan;
      if (p?.hand) { setFileDialog(null); setHandQuote(no); return 'hand'; } // 수기 견적 → 견적서 창 (지금 도면은 그대로)
      if (!confirmDiscard()) return null;
      if (!p?.form) throw new Error('도면 데이터가 비어 있습니다');
      applyPlan(p);
      setEstiNo(data.estiNo); setSavedSig(JSON.stringify(p));
      setFileDialog(null);
      flash(true, `불러왔습니다 — ${data.estiNo}`);
      return 'drawing';
    } catch (e) { flash(false, `불러오기 실패: ${errMsg(e)}`); return null; }
  };

  // 체험 도면: 체험 중에는 브라우저에 계속 보관 → 가입·로그인 후 처음 열 때 이어받아 저장할 수 있게
  const GUEST_KEY = 'pd_guest_plan';
  useEffect(() => {
    if (!isGuest || !generated) return;
    try { localStorage.setItem(GUEST_KEY, planSig); } catch { /* 저장소 없음 */ }
  }, [isGuest, generated, planSig]);
  useEffect(() => {
    if (isGuest) return;
    let p: any = null;
    try { p = JSON.parse(localStorage.getItem(GUEST_KEY) || 'null'); localStorage.removeItem(GUEST_KEY); } catch { /* 없음 */ }
    if (p?.form) {
      applyPlan(p);
      flash(true, '체험 중 그린 도면을 이어왔습니다 — 💾 저장하면 보관됩니다');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 패널 수량 집계용: 다각형 패널을 면별로 묶음 (하리 안쪽 패널은 가까운 면에 포함)
  const summaryWalls = useMemo(() => {
    if (!poly) return walls;
    const w: Walls = { left: [], right: [], top: [], bottom: [] };
    poly.panels.forEach(pp => {
      const horiz = Math.abs(pp.b.x - pp.a.x) > Math.abs(pp.b.y - pp.a.y);
      const cx = (pp.a.x + pp.b.x) / 2, cy = (pp.a.y + pp.b.y) / 2;
      const side = horiz ? (cy < form.VERTICAL / 2 ? 'top' : 'bottom') : (cx < form.HORIZONTAL / 2 ? 'left' : 'right');
      w[side].push({ x: 0, y: 0, width: 0, height: 0, size: pp.size, isCustom: pp.size !== config.DEFAULT_SIZE,
        wallSide: side, index: w[side].length, grooveDir: 'RIGHT', leftType: 'yangsu', rightType: 'eumsu' } as Panel);
    });
    return w;
  }, [poly, walls, form.HORIZONTAL, form.VERTICAL, config.DEFAULT_SIZE]);

  if (showHariTest) return <HariClockwiseTest onBack={() => setShowHariTest(false)} />;
  if (scanCode) return <PanelScan code={scanCode} onClose={closeScan} onBoard={no => { closeScan(); if (user.role === 'PROD') setProd(no); else setBoard(no); }} />;
  // 생산 직원: 생산 화면만 (금액·견적·거래처 없음)
  if (user.role === 'PROD') return <>
    <ProductionBoard key={String(prod)} initial={prod === true ? null : prod} user={`${user.compName} · ${user.name}`}
      onPassword={() => setFileDialog('password')} onLogout={logout} onNotice={() => setBoard2({})} />
    {fileDialog === 'password' && <PasswordDialog onClose={() => setFileDialog(null)} />}
    {board2 && <BoardDialog initialPost={board2.post} onClose={() => setBoard2(null)} />}
  </>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--pd-bg)', overflow: 'hidden' }}>
      <header className="pd-header">
        <button className="pd-btn icon ghost" onClick={() => setSidebarOpen(prev => !prev)} title={sidebarOpen ? '설정판 접기' : '설정판 열기'}>☰</button>
        <div className="pd-brand"><img src="/icon.svg" alt="" />Panel Draw</div>
        <span className="pd-doc" title={estiNo ?? ''}>
          {estiNo ? `${estiNo}${drawingInfo.siteName ? ' · ' + drawingInfo.siteName : ''}` : generated ? '새 도면' : ''}
        </span>
        {generated && (estiNo ? dirty : true) && !isGuest && <span className="pd-chip warn dot" style={{ cursor: 'default' }}>{estiNo ? '변경됨' : '저장 안 됨'}</span>}
        {shownIssues.length > 0 && <button className="pd-chip warn" onClick={() => setShowIssues(v => !v)} title="만들 수 없는 배치">⚠ 확인 필요 {shownIssues.length}</button>}
        <span className="pd-spacer" />
        {generated && <button className="pd-btn ghost" onClick={() => setShow3d(true)} title="패널을 한 장씩 입체로 — 돌려 보기 · 패널 번호 확인">🧊 3D 보기</button>}
        {isGuest && <>
          <span className="pd-chip on" style={{ cursor: 'default' }}>무료 체험 중</span>
          <button className="pd-btn primary" onClick={toSignup}>회사 가입 신청</button>
          <button className="pd-btn" onClick={toLogin}>로그인</button>
          <button className="pd-btn ghost" onClick={logout}>홈으로</button>
        </>}
        {!isGuest && <>
          <button className="pd-btn ghost" onClick={() => setHome(true)} title="이번 달 현황 · 견적 목록 · 통계">🏠 홈</button>
          <button className="pd-btn ghost" onClick={() => setFileDialog('load')}>📂 불러오기</button>
          <button className="pd-btn ghost" onClick={() => setBoard(estiNo ?? true)}>📋 작업 현황</button>
          <button className="pd-btn ghost" onClick={() => setProd(estiNo ?? true)} title="생산 라인 화면 — 패널 체크·재단표·도면·메모 (금액 없음)">🏭 생산</button>
          {generated && <button className="pd-btn weak" onClick={() => {
            if (!estiNo || dirty) { flash(false, '견적서는 저장한 도면만 — 먼저 저장하세요'); return; }
            setFileDialog('quote');
          }} title="도면 면적·문으로 견적 금액 계산 → 인쇄·확정·입금">견적서</button>}
          {generated && <button className="pd-btn weak" onClick={() => {
            if (!estiNo || dirty) { flash(false, '작업지시는 저장한 도면만 — 먼저 저장하세요'); return; }
            if (shownIssues.length) { flash(false, `확인 필요 ${shownIssues.length}건을 먼저 고치세요 — 만들 수 없는 배치로 생산할 수 없습니다`); setShowIssues(true); return; }
            setOrderOpen(true);
          }} title="이 도면의 패널을 번호별로 등록하고 작업 시작">작업지시</button>}
          <button className="pd-btn primary" onClick={() => handleSave(false)} disabled={saving || !generated} title="현재 견적번호에 저장 (없으면 새로)">
            {saving ? '저장 중…' : '저장'}</button>
          <div className="pd-menu-wrap">
            <button className="pd-btn ghost" onClick={() => setMenuOpen(v => !v)} title={user.id}>
              <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.compName}</span>
              {pending + newInq > 0 && <span className="pd-badge">{pending + newInq}</span>} ▾
            </button>
            {menuOpen && <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 1400 }} onClick={() => setMenuOpen(false)} />
              <div className="pd-menu" onClick={() => setMenuOpen(false)}>
                <div className="who"><b>{user.name}</b><span>{user.compName} · {user.id}</span></div>
                {estiNo && <button onClick={() => handleSave(true)} disabled={saving}>새 견적번호로 복사 저장</button>}
                <button onClick={() => setFileDialog('biz')}>📒 거래처 · 입금 · 단가</button>
                <button onClick={() => setBoard2({})}>📢 게시판 (공지 · 자료실)</button>
                {(user.role === 'SUPER' || user.role === 'OWNER') && <button onClick={() => setFileDialog('staff')}>직원 관리</button>}
                {user.role === 'SUPER' && <>
                  <hr />
                  <button onClick={() => setFileDialog('admin')}>가입 승인 {pending > 0 && <span className="pd-badge">{pending}</span>}</button>
                  <button onClick={() => setFileDialog('inquiry')}>문의함 {newInq > 0 && <span className="pd-badge">{newInq}</span>}</button>
                  <button onClick={() => setShowHariTest(true)}>🧪 하리 테스트</button>
                </>}
                <hr />
                <button onClick={() => window.open('/guide/', '_blank', 'noopener')}>📖 사용 안내</button>
                <button onClick={() => setFileDialog('password')}>내 정보 · 비밀번호</button>
                <button onClick={() => { if (confirmDiscard()) logout(); }} style={{ color: 'var(--pd-red)' }}>로그아웃</button>
              </div>
            </>}
          </div>
        </>}
      </header>
      {notice && <div className={`pd-toast${notice.ok ? '' : ' err'}`}>{notice.ok ? '✓' : '⚠'} {notice.text}</div>}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 0 }}>
        {/* 좌측 설정판 */}
        <div className="pd-side" style={{
          width: sidebarOpen ? 300 : 0,
          flexShrink: 0,
          overflow: 'auto',
          transition: 'width 0.2s ease',
        }}>
          {sidebarOpen && (
            <div style={{ padding: '12px 0 16px 12px' }}>
              <InputPanel form={form} onChange={setForm} onGenerate={handleGenerate} />
              <ShapeEditor H={form.HORIZONTAL} V={form.VERTICAL} cuts={cuts} onChange={c => { setCuts(c); setPolyEdits({}); setPreviewEdits(null); setSelectedPolyId(null); }} />

              {generated && (doors.length > 0 || drains.length > 0) && (
                <div className="pd-card" style={{ marginTop: 10 }}>
                  {doors.length > 0 && <>
                    <h3 className="pd-title">문 <small>{doors.length}개</small></h3>
                    {doors.map(door => (
                      <div key={door.id} className="pd-item">
                        <span>{sideNames[door.wallSide]} · {door.width}mm · 위치 {door.position}</span>
                        <button className="pd-del" onClick={() => handleDeleteDoor(door.id)}>삭제</button>
                      </div>
                    ))}
                  </>}
                  {drains.length > 0 && <>
                    <h3 className="pd-title" style={{ marginTop: doors.length ? 16 : 0 }}>배수구 <small>{drains.length}개</small></h3>
                    {drains.map(drain => (
                      <div key={drain.id} className="pd-item">
                        <span>X {drain.x} · Y {drain.y} · {drain.size}mm</span>
                        <button className="pd-del" onClick={() => handleDeleteDrain(drain.id)}>삭제</button>
                      </div>
                    ))}
                  </>}
                </div>
              )}

              {generated && (
                <div className="pd-card" style={{ marginTop: 10 }}>
                  <h3 className="pd-title">인쇄</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button className="pd-btn block" onClick={() => printDrawing({
                      horizontal: form.HORIZONTAL, vertical: form.VERTICAL,
                      panelThick: form.PANEL_THICK, walls, doors, drains, config, drawingInfo, poly,
                      scene: sceneExportRef.current,
                    })}>🖨 도면 인쇄 · PDF</button>
                    {poly && <button className="pd-btn block" onClick={() => printProductionSheet({
                      rows: sheetRows,
                      info: drawingInfo,
                      spec: { horizontal: form.HORIZONTAL, vertical: form.VERTICAL, height: form.HEIGHT, wallHeight: form.WALL_HEIGHT,
                        thick: form.PANEL_THICK, material: getMaterialSpec(form.MATERIAL).label, ceiling: form.CEILING_YN === 'Y', floor: form.FLOOR_YN === 'Y' },
                      doors, drains, middleWalls, scene: sceneExportRef.current,
                    })}>생산지시서 · 재단표</button>}
                    {poly && <button className="pd-btn block" title="A4 16칸(2×8) 라벨 — 패널 번호·QR" onClick={() => {
                      if (isGuest) { flash(false, '라벨·작업 관리는 가입 후 쓸 수 있습니다 — 위쪽 [회사 가입 신청]'); return; }
                      if (!estiNo || dirty) { flash(false, '라벨은 저장한 도면만 인쇄할 수 있습니다 — 먼저 저장하세요'); return; }
                      printLabels({ items: labelItems(poly.panels, ceilingPanels, form.FLOOR_YN === 'Y', form.WALL_HEIGHT), estiNo,
                        siteName: drawingInfo.siteName, material: getMaterialSpec(form.MATERIAL).label, thick: form.PANEL_THICK });
                    }}>패널 라벨 (QR)</button>}
                  </div>
                </div>
              )}

              {generated && <DrawingInfoPanel info={drawingInfo} onChange={setDrawingInfo} />}
              {generated && <PanelSummary walls={summaryWalls} />}
            </div>
          )}
        </div>

        {/* 우측: 도구 막대 + 도면 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <div style={{ padding: '12px 12px 10px', flexShrink: 0 }}>
            <Toolbar activeTool={activeTool} onToolChange={setActiveTool} generated={generated}
              middleWallDir={middleWallDir} onMiddleWallDirChange={setMiddleWallDir} />
          </div>
          {showIssues && shownIssues.length > 0 && (
            <div className="pd-warn-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <b>만들 수 없는 배치 {shownIssues.length}건 — 고친 뒤 저장·작업지시 하세요</b>
                <button onClick={() => setShowIssues(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, color: '#8B95A1' }}>✕</button>
              </div>
              {shownIssues.map((i, k) => <div key={k}>· {i.msg}</div>)}
            </div>
          )}
          <div className="pd-stage">
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flexShrink: 0, flexWrap: 'wrap', gap: 6 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--pd-text)', whiteSpace: 'nowrap' }}>도면</h2>
              {generated && (
                <span style={{ fontSize: 13, color: 'var(--pd-mute)', marginLeft: 6 }}>
                  {form.HORIZONTAL} × {form.VERTICAL} · {form.PANEL_THICK}T · 문 {doors.length} · 배수구 {drains.length}
                </span>
              )}
              {generated && (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--pd-mute)', flexWrap: 'wrap', whiteSpace: 'nowrap' }}>
                  패널 흐름
                  {(['ccw', 'cw'] as const).map(f => (
                    <button key={f} className={`pd-chip${flow === f ? ' on' : ''}`} onClick={() => setFlow(f)}>{f === 'ccw' ? '↺ 반시계' : '↻ 시계'}</button>
                  ))}
                  <button className={`pd-chip${showNumbers ? ' on' : ''}`} onClick={() => setShowNumbers(v => !v)} title="패널 안 글자를 사이즈 ↔ 패널 번호로 바꿈" style={{ marginLeft: 6 }}># 번호</button>
                  {ceiling && <span style={{ marginLeft: 10 }}>천장 흐름</span>}
                  {ceiling && (['lo', 'hi'] as const).map(f => (
                    <button key={f} className={`pd-chip${ceilingFlow === f ? ' on' : ''}`} onClick={() => setCeilingFlow(f)}>
                      {form.CEILING_HV === 'H' ? (f === 'lo' ? '↑ 위로' : '↓ 아래로') : (f === 'lo' ? '← 왼쪽' : '→ 오른쪽')}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <DrawingCanvas
                horizontal={generated ? form.HORIZONTAL : 0}
                vertical={generated ? form.VERTICAL : 0}
                walls={generated ? walls : emptyWalls}
                config={config}
                panelThick={form.PANEL_THICK}
                doors={effDoors}
                drains={drains}
                activeTool={activeTool}
                ceilingPanels={ceilingPanels}
                ceilingDirection={form.CEILING_HV as 'H' | 'V'}
                onWallClick={handleWallClick}
                onFloorClick={activeTool === 'middleWall' ? handleMiddleWallPlace : handleFloorClick}
                onPanelClick={handlePanelClick}
                onCeilingPanelClick={handleCeilingPanelClick}
                onDoorClick={handleDoorClick}
                onDrainMove={handleDrainMove}
                onDrainClick={handleDrainClick}
                onDrainDistanceEdit={(id, _dir, _val) => {}}
                middleWalls={middleWalls}
                onMiddleWallClick={handleMiddleWallClick}
                onMiddleWallMove={handleMiddleWallMove}
                haris={haris}
                onHariPlace={handleHariPlace}
                onHariClick={(id) => { const h = haris.find(h => h.id === id); if (h) setHariEdit(h); }}
                poly={poly}
                onPolyPanelClick={(id) => { setPreviewEdits(null); setSegPieceKey(null); setSelectedPolyId(id); }}
                selectedPolyId={selectedPolyId}
                existingWalls={existingWalls}
                doorEnds={poly?.doorEnds}
                onDoorDrag={handleDoorDrag}
                panelNos={panelNos}
                exportRef={sceneExportRef}
                doorGuide={doorGuide}
                onEdgeClick={(key, from, to) => {
                  const [p0, p1] = key.split('-').map(t => t.split(',').map(Number));
                  setExistingEdit({ key, lo: from, hi: to, horiz: p0[1] === p1[1] });
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {board && <WorkBoard initial={board === true ? null : board} onClose={() => setBoard(null)}
        onOpen={no => handleLoad(no).then(k => { if (k === 'drawing') { setBoard(null); setHome(false); } })} />}
      {home && !isGuest && <HomeBoard onClose={() => setHome(false)} onNewDrawing={() => setHome(false)} onBoard={no => setBoard(no)} onNotice={id => setBoard2({ post: id })}
        onOpen={no => handleLoad(no).then(k => { if (k === 'drawing') setHome(false); })} />}
      {handQuote && <QuoteDialog estiNo={handQuote} print={{ siteName: '' }} onClose={() => setHandQuote(null)} />}
      {orderOpen && estiNo && poly && <WorkOrderDialog estiNo={estiNo}
        items={labelItems(poly.panels, ceilingPanels, form.FLOOR_YN === 'Y', form.WALL_HEIGHT)}
        onClose={() => setOrderOpen(false)} onDone={m => {
          setOrderOpen(false); flash(true, m);
          // 생산 화면에서 볼 도면 그림 (실패해도 작업지시는 그대로)
          const scene = sceneExportRef.current;
          if (scene) try { uploadPlanImage(estiNo, scene(1500, 1000).toDataURL('image/png')).catch(() => flash(false, '도면 그림을 생산 화면에 올리지 못했습니다 — 작업지시를 다시 내려 주세요')); } catch { /* 그림 못 만듦 */ }
        }} />}
      {prod && <ProductionBoard key={String(prod)} initial={prod === true ? null : prod} onClose={() => setProd(null)} onNotice={() => setBoard2({})} />}
      {board2 && <BoardDialog initialPost={board2.post} onClose={() => setBoard2(null)} />}
      {show3d && model3d && <React.Suspense fallback={<div className="pd-toast">3D 보기 여는 중…</div>}>
        <Plan3D model={model3d} title={estiNo ? `${estiNo}${drawingInfo.siteName ? ' · ' + drawingInfo.siteName : ''}` : drawingInfo.siteName}
          onClose={() => setShow3d(false)} />
      </React.Suspense>}
      {fileDialog === 'load' && <LoadDialog current={estiNo} onLoad={handleLoad} onClose={() => setFileDialog(null)} />}
      {fileDialog === 'password' && <PasswordDialog onClose={() => setFileDialog(null)} />}
      {existingEdit && <ExistingWallDialog key={existingEdit.key} edge={existingEdit} onClose={() => setExistingEdit(null)}
        current={existingWalls.filter(o => o.edge === existingEdit.key)}
        onApply={segs => setExistingWalls(prev => [...prev.filter(o => o.edge !== existingEdit.key),
          ...segs.map(g => ({ edge: existingEdit.key, from: g.from, to: g.to, kind: 'existing' as const }))])} />}
      {fileDialog === 'quote' && estiNo && <QuoteDialog estiNo={estiNo} onClose={() => setFileDialog(null)}
        auto={{ material: form.MATERIAL, thick: form.PANEL_THICK, height: form.HEIGHT || form.WALL_HEIGHT, ceiling: form.CEILING_YN === 'Y', floor: form.FLOOR_YN === 'Y',
          outline: outline.pts, middleWalls, doors }}
        print={{ siteName: drawingInfo.siteName, spec: { material: getMaterialSpec(form.MATERIAL).label, thick: form.PANEL_THICK, horizontal: form.HORIZONTAL,
          vertical: form.VERTICAL, height: form.HEIGHT || form.WALL_HEIGHT, ceiling: form.CEILING_YN === 'Y', floor: form.FLOOR_YN === 'Y', partitions: middleWalls.length } }} />}
      {fileDialog === 'biz' && <BizDialog manager={user.role === 'SUPER' || user.role === 'OWNER'} onClose={() => setFileDialog(null)}
        onOpenEstimate={no => { setFileDialog(null); handleLoad(no); }} />}
      {fileDialog === 'admin' && <CompanyAdminDialog onClose={() => setFileDialog(null)}
        onChanged={() => pendingCount().then(r => setPending(r.data.count)).catch(() => {})} />}
      {fileDialog === 'inquiry' && <InquiryDialog onClose={() => setFileDialog(null)}
        onChanged={() => pendingCount().then(r => { setPending(r.data.count); setNewInq(r.data.inquiries); }).catch(() => {})} />}
      {fileDialog === 'staff' && <StaffDialog me={user.id} compName={user.compName} onClose={() => setFileDialog(null)} />}

      {/* 문 수정 다이얼로그 */}
      {doorDialog ? (
        <DoorDialog
          wallSide={doorDialog!.door.wallSide}
          wallLength={getWallLength(doorDialog!.door.wallSide)}
          door={doorDialog!.door}
          config={config}
          onConfirm={handleDoorConfirm}
          onDelete={handleDoorDelete}
          onCancel={() => { setDoorDialog(null); setDoorPreview(null); }}
          panelThick={form.PANEL_THICK}
          minPanel={fillSpec.min}
          check={checkDoorAt}
          onPreview={setDoorPreview}
        />
      ) : null}

      {/* 배수구 수정 다이얼로그 */}
      {drainEditDialog && (
        <DrainEditDialog
          drain={drainEditDialog!}
          horizontal={form.HORIZONTAL}
          vertical={form.VERTICAL}
          panelThick={form.PANEL_THICK}
          onConfirm={handleDrainUpdate}
          onDelete={handleDrainDeleteFromEdit}
          onCancel={() => setDrainEditDialog(null)}
        />
      )}

      {/* 패널 수정 다이얼로그 */}
      {(() => {
        const sel = selectedPolyId && polyBase ? polyBase.panels.find(p => p.id === selectedPolyId) : null;
        if (!sel || !polyBase) return null;
        const piece = polyBase.pieces[sel.pieceKey];
        return (
          <PolyPanelDialog
            key={sel.id}
            panel={sel}
            piece={piece}
            ascending={(sel.b.x - sel.a.x) + (sel.b.y - sel.a.y) > 0}
            edits={polyEdits}
            spec={fillSpec}
            previewError={poly?.pieces[sel.pieceKey]?.error}
            onPreview={e => setPreviewEdits(e)}
            onApply={e => { setPolyEdits(e); setPreviewEdits(null); setSelectedPolyId(null); }}
            onCancel={() => { setPreviewEdits(null); setSelectedPolyId(null); }}
            onOpenSegment={() => { setPreviewEdits(null); setSelectedPolyId(null); setSegPieceKey(sel.pieceKey); }}
            onEditPartition={sel.pieceKey.startsWith('part:') ? () => {
              const mw = middleWalls.find(w => sel.pieceKey.startsWith(`part:${w.id}#`));
              setPreviewEdits(null); setSelectedPolyId(null); if (mw) setMiddleWallEdit(mw);
            } : undefined}
          />
        );
      })()}
      {(() => {
        const piece = segPieceKey && polyBase ? polyBase.pieces[segPieceKey] : null;
        if (!piece || !segPieceKey) return null;
        const withRule = (rule: SegRule | null): PolyEdits => {
          const rules = { ...(polyEdits.rules ?? {}) };
          if (rule) rules[segPieceKey] = rule; else delete rules[segPieceKey];
          return { ...polyEdits, rules };
        };
        return (
          <SegmentFillDialog
            key={segPieceKey}
            label={`${piece.horiz ? '가로' : '세로'} 조각 (${piece.total}mm)`}
            total={piece.total}
            orient={piece.horiz ? 'h' : 'v'}
            initial={polyEdits.rules?.[segPieceKey] ?? defaultRule(fillSpec)}
            spec={fillSpec}
            onPreview={rule => setPreviewEdits(rule ? withRule(rule) : null)}
            onApply={rule => {
              // 구간을 다시 채우면 접합 뒤집기는 그 조각에서 초기화 (패널 수·순번이 바뀔 수 있음)
              const e = withRule(rule);
              const flips = { ...(e.flips ?? {}) };
              Object.keys(flips).forEach(k => { if (k.startsWith(segPieceKey + ':j')) delete flips[k]; });
              setPolyEdits({ ...e, flips }); setPreviewEdits(null); setSegPieceKey(null);
            }}
            onCancel={() => { setPreviewEdits(null); setSegPieceKey(null); }}
          />
        );
      })()}
      {panelEdit ? (
        <PanelEditDialog
          panel={(panelEdit as any).panel}
          onConfirm={handlePanelUpdate}
          onCancel={() => setPanelEdit(null)}
        />
      ) : null}

      {/* 칸막이 수정 다이얼로그 */}
      {middleWallEdit && (
        <MiddleWallEditDialog
          middleWall={middleWallEdit!}
          horizontal={form.HORIZONTAL}
          vertical={form.VERTICAL}
          panelThick={form.PANEL_THICK}
          onConfirm={handleMiddleWallUpdate}
          onDelete={handleMiddleWallDelete}
          onCancel={() => setMiddleWallEdit(null)}
        />
      )}

      {/* 하리 수정 다이얼로그 */}
      {hariEdit && (
        <HariEditDialog
          hari={hariEdit!}
          horizontal={form.HORIZONTAL}
          vertical={form.VERTICAL}
          panelThick={form.PANEL_THICK}
          onConfirm={handleHariUpdate}
          onDelete={handleHariDelete}
          onCancel={() => setHariEdit(null)}
        />
      )}

      {/* 천장 패널 수정 다이얼로그 */}
      {ceilingEdit && !ceilingSeg && ceilingBase?.panels[ceilingEdit.index] ? (
        <CeilingPanelEditDialog
          key={ceilingEdit.index}
          panel={ceilingBase.panels[ceilingEdit.index]}
          count={ceilingBase.panels.length}
          direction={form.CEILING_HV as 'H' | 'V'}
          flow={ceilingFlow}
          rule={ceilingRule}
          spec={fillSpec}
          previewError={ceiling?.error}
          onPreview={r => setCeilingPreview(r)}
          onApply={r => { setCeilingRule(r); closeCeiling(); }}
          onDirection={d => { setForm(prev => ({ ...prev, CEILING_HV: d })); setCeilingRule(null); closeCeiling(); }}
          onFlow={setCeilingFlow}
          edge={ceilingEdge}
          edgeSizeIfOn={ceilingEdgeSize(form.PANEL_THICK)}
          onEdge={on => { setCeilingEdgeOn(on); setCeilingRule(null); closeCeiling(); }}
          onOpenSegment={() => { setCeilingPreview(undefined); setCeilingSeg(true); }}
          onCancel={closeCeiling}
        />
      ) : null}
      {ceilingSeg && ceiling && (
        <SegmentFillDialog
          label={`천장 ${form.CEILING_HV === 'H' ? '가로' : '세로'} 패널${ceilingEdge ? ` — 첫 패널 ${ceilingEdge} 다음부터` : ' 전체'} (${ceiling.total}mm)`}
          total={ceilingEdge ? ceiling.total - ceilingEdge : ceiling.total + fillSpec.space}
          orient={form.CEILING_HV === 'H' ? 'v' : 'h'}
          initial={ceilingRule ?? defaultRule(fillSpec)}
          spec={fillSpec}
          onPreview={rule => setCeilingPreview(rule ?? undefined)}
          onApply={rule => { setCeilingRule(rule); closeCeiling(); }}
          onCancel={closeCeiling}
        />
      )}
    </div>
  );
}

export default App;
